import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BASE_API_URL_V2 } from "@/constants";
import fpFetch from "@/services/fpFetch";
import { RMS_BOOTSTRAP_QUERY_KEY } from "./bootstrap";

// API base URL
const API_BASE = BASE_API_URL_V2;

// ============= Types =============

export interface DocumentColumn {
  id: number;
  name: string;
  position: number;
  data_type: "string" | "number" | "percentage" | "boolean" | "date" | "entity";
  description?: string | null;
  is_enum: boolean;
  scalar_values?: string[] | null;
  enum_colors?: Record<string, string> | null;
  entity_types?: string[] | null;
  is_array: boolean;
  ai_autofill: boolean;
  default_value?: unknown | null;
}

export interface DocumentColumnSchema {
  columns: DocumentColumnDefinition[];
}

export interface DocumentColumnDefinition {
  id?: number | null;
  name: string;
  position: number;
  data_type: "string" | "number" | "percentage" | "boolean" | "date" | "entity";
  description?: string | null;
  is_enum: boolean;
  scalar_values?: string[] | null;
  enum_colors?: Record<string, string> | null;
  entity_types?: string[] | null;
  is_array: boolean;
  ai_autofill: boolean;
  default_value?: unknown | null;
}

// ============= API Functions =============

// The acting organization's columns: another organization's are edited inside
// it (#10718).
export const fetchDocumentColumns = async (): Promise<DocumentColumn[]> => {
  const response = await fpFetch(`${API_BASE}/api/v2/rms/documents/columns`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) throw new Error("Failed to fetch document columns");
  return response.json();
};

export const updateDocumentColumns = async (
  payload: DocumentColumnSchema
): Promise<DocumentColumn[]> => {
  const response = await fpFetch(`${API_BASE}/api/v2/rms/documents/columns`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error("Failed to update document columns");
  return response.json();
};

// ============= React Query Hooks =============

export const DOCUMENT_COLUMNS_QUERY_KEYS = {
  all: ["document-columns"] as const,
  columns: () => [...DOCUMENT_COLUMNS_QUERY_KEYS.all, "columns"] as const,
};

export const useDocumentColumns = () => {
  return useQuery({
    queryKey: DOCUMENT_COLUMNS_QUERY_KEYS.columns(),
    queryFn: fetchDocumentColumns,
    staleTime: 60 * 1000, // 1 minute
  });
};

export const useUpdateDocumentColumns = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payload }: { payload: DocumentColumnSchema }) =>
      updateDocumentColumns(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: DOCUMENT_COLUMNS_QUERY_KEYS.columns(),
      });
      // The document schema the bootstrap carries is built from these columns.
      queryClient.invalidateQueries({ queryKey: RMS_BOOTSTRAP_QUERY_KEY });
    },
  });
};
